<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import dayjs from 'dayjs';
import type { ContractManagementExtractionInDb, ContractManagementViewColumnInDb } from '@/api';
import { CalendarService } from '@/services/calendarService';
import type { DateRecurrence } from '@/services/contractManagement/dateRow';
import {
  buildExtractionCell,
  eventsFromPreviewRow,
  occurrencesInRange,
  type CalendarPreviewEvent,
  type CalendarPreviewFilters,
} from '@/utils/calendarOccurrences';
import { logger } from '@/utils/logger';
import ContractManagementExtractionDetailsModal from '@/components/contractManagement/ContractManagementExtractionDetailsModal.vue';
import GaiaButton from '../../../base/GaiaButton.vue';
import GaiaCalendar from '../../../base/GaiaCalendar.vue';
import GaiaLink from '../../../base/GaiaLink.vue';
import GaiaMenuSurface from '../../../base/GaiaMenuSurface.vue';
import GaiaPopover from '../../../base/GaiaPopover.vue';
import GaiaSpinner from '../../../base/GaiaSpinner.vue';
import GaiaText from '../../../base/GaiaText.vue';
import GaiaEmptyState from '../../../composite/GaiaEmptyState.vue';
import { gaiaAccent, gaiaNeutral } from '../../../themes/tokens';
import { contractFileRoute, contractRoute } from '@/router/links';
import {
  ACCESS_PERMISSION_READ,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
} from '@/utils/accessPermission';

/**
 * A calendar's dates as a month grid: the fixed contract dates it picked and
 * the dates its prompt extracted, recurring ones repeated on every occurrence.
 * Clicking an entry opens its details — the contract, the file, and for an
 * extracted date the way to correct it.
 */
const props = defineProps<{ calendarUid: string; filters: CalendarPreviewFilters }>();

/** A date was corrected here, so a list beside the grid is stale. */
const emit = defineEmits<{ changed: [] }>();

/** Entries a day cell shows before folding the rest into "+N more". */
const VISIBLE_PER_DAY = 3;
const DAY = 'YYYY-MM-DD';

const events = ref<CalendarPreviewEvent[]>([]);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);

const hasFilters = computed(
  () =>
    props.filters.text.trim().length > 0 ||
    props.filters.onlyValueExtracted ||
    props.filters.onlyFuture ||
    props.filters.types.length > 0
);

const today = dayjs();
const visibleMonth = ref<{ year: number; month: number }>({
  year: today.year(),
  month: today.month() + 1,
});

/** The six weeks the grid shows, Monday first — adjacent-month days included. */
const visibleRange = computed(() => {
  const first = dayjs(new Date(visibleMonth.value.year, visibleMonth.value.month - 1, 1));
  const from = first.subtract((first.day() + 6) % 7, 'day');
  return { from, to: from.add(41, 'day') };
});

/** Guards against an older month's response landing after a newer one. */
let requestId = 0;

async function fetchAll(): Promise<void> {
  const id = ++requestId;
  loading.value = true;
  loadError.value = null;
  try {
    const { from, to } = visibleRange.value;
    const result = await CalendarService.previewRange(props.calendarUid, {
      start: from.format(DAY),
      // `end` is exclusive: the day after the grid's last cell.
      end: to.add(1, 'day').format(DAY),
      text: props.filters.text,
      only_future: props.filters.onlyFuture,
    });
    if (id !== requestId) return;
    const collected: CalendarPreviewEvent[] = [];
    for (const row of result.results) {
      // The range endpoint has no type filter either, so it runs here too.
      const contractType = row[0].contract.contract_type?.name ?? '';
      if (props.filters.types.length > 0 && !props.filters.types.includes(contractType)) {
        continue;
      }
      const rowEvents = eventsFromPreviewRow(row);
      // The range endpoint has no `only_value_extracted`: drop rows without an
      // extracted date here instead.
      if (
        props.filters.onlyValueExtracted &&
        !rowEvents.some(event => event.source === 'extracted')
      ) {
        continue;
      }
      collected.push(...rowEvents);
    }
    events.value = collected;
  } catch (error) {
    if (id !== requestId) return;
    logger.error('Error loading calendar preview:', error);
    loadError.value = error instanceof Error ? error.message : 'Failed to load calendar preview';
  } finally {
    if (id === requestId) loading.value = false;
  }
}

const eventsByDay = computed(() => {
  const byDay = new Map<string, CalendarPreviewEvent[]>();
  const { to } = visibleRange.value;
  // `only_future` keeps a contract that has any upcoming date, so its past
  // occurrences would still fill earlier days; the grid drops those too.
  const from =
    props.filters.onlyFuture && visibleRange.value.from.isBefore(today, 'day')
      ? today
      : visibleRange.value.from;
  for (const event of events.value) {
    for (const day of occurrencesInRange(event, from, to)) {
      const list = byDay.get(day);
      if (list) list.push(event);
      else byDay.set(day, [event]);
    }
  }
  return byDay;
});

function dayKey(year: number, month: number, date: number): string {
  return dayjs(new Date(year, month - 1, date)).format(DAY);
}

function onPanelChange(info: { year: number; month: number }): void {
  visibleMonth.value = info;
}

function contractName(event: CalendarPreviewEvent): string {
  return event.file.contract.name;
}

/*
 * A calendar's viewers see its dates without necessarily being able to open
 * what they come from: the links go inert, with the reason as their title.
 * A contract file carries its contract's bits, on its vault file.
 */
function contractBlock(event: CalendarPreviewEvent): string | null {
  return deniedReason(event.file.contract.permissions, ACCESS_PERMISSION_READ);
}

function fileBlock(event: CalendarPreviewEvent): string | null {
  return deniedReason(
    event.file.vault_file?.permissions ?? event.file.contract.permissions,
    ACCESS_PERMISSION_READ
  );
}

/** Correcting the extracted date writes to the file, so it needs WRITE on it. */
function extractionBlock(event: CalendarPreviewEvent): string | null {
  return deniedReason(
    event.file.vault_file?.permissions ?? event.file.contract.permissions,
    ACCESS_PERMISSION_WRITE
  );
}

function fileName(event: CalendarPreviewEvent): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return event.file.vault_file?.current_revision?.file_name || 'Untitled file';
}

const FREQUENCY_LABEL: Record<string, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
};

function recurrenceText(recurrence: DateRecurrence | null): string | null {
  const type = recurrence?.recurrence_type ?? 'never';
  if (!recurrence?.recurrence_frequency || type === 'never') return null;
  const interval = recurrence.recurrence_interval ?? 1;
  const unit = FREQUENCY_LABEL[recurrence.recurrence_frequency];
  const every = interval > 1 ? `Every ${interval} ${unit}s` : `Every ${unit}`;
  if (type === 'count') return `${every}, ${recurrence.recurrence_count ?? 1} times`;
  if (type === 'until_date') {
    return `${every} until ${dayjs(recurrence.recurrence_until_date).format('D MMM YYYY')}`;
  }
  return every;
}

// ── Popover ───────────────────────────────────────────────────────────────
// One popover for the whole grid, placed at the click: a popover per chip
// would mount dozens of them for a busy month.

const popover = ref<{
  show: boolean;
  x: number;
  y: number;
  day: string;
  event: CalendarPreviewEvent | null;
}>({ show: false, x: 0, y: 0, day: '', event: null });

function openEvent(mouse: MouseEvent, day: string, event: CalendarPreviewEvent): void {
  popover.value = { show: true, x: mouse.clientX, y: mouse.clientY, day, event };
}

function openDay(mouse: MouseEvent, day: string): void {
  popover.value = { show: true, x: mouse.clientX, y: mouse.clientY, day, event: null };
}

function closePopover(): void {
  popover.value = { ...popover.value, show: false };
}

const popoverDayEvents = computed(() => eventsByDay.value.get(popover.value.day) ?? []);

// ── Extraction modal ──────────────────────────────────────────────────────

const showEditModal = ref<boolean>(false);
const editingCell = ref<{
  columnUid: string;
  extraction: ContractManagementExtractionInDb | null;
  column: ContractManagementViewColumnInDb | null;
  fileUid?: string;
}>({ columnUid: '', extraction: null, column: null });

function editExtraction(event: CalendarPreviewEvent): void {
  if (!event.extraction) return;
  editingCell.value = buildExtractionCell(event.extraction, event.file.uid);
  closePopover();
  showEditModal.value = true;
}

async function handleExtractionUpdate(): Promise<void> {
  showEditModal.value = false;
  emit('changed');
  await fetchAll();
}

function refresh(): void {
  void fetchAll();
}
defineExpose({ refresh });

onMounted(fetchAll);
watch([(): CalendarPreviewFilters => props.filters, visibleRange], fetchAll);

// Colours go inline rather than through `v-bind()` in the CSS: the "+N more"
// list renders the same chips inside a popover, which teleports to <body>,
// outside the root the CSS variables are declared on.
function chipStyle(event: CalendarPreviewEvent): Record<string, string> {
  return event.source === 'extracted'
    ? { borderLeftColor: gaiaAccent.base, background: gaiaAccent.wash, color: gaiaNeutral.text }
    : { borderLeftColor: gaiaNeutral.textFaint, color: gaiaNeutral.text };
}

const chipHover = gaiaNeutral.surfaceHover;
const moreText = gaiaNeutral.textSubtle;
const moreHover = gaiaNeutral.ink;
</script>

<template>
  <n-flex vertical :size="10" :wrap="false">
    <gaia-empty-state v-if="loadError" status="alert" title="Couldn’t load the preview">
      {{ loadError }}
      <template #actions>
        <gaia-button type="primary" size="small" @click="fetchAll">Retry</gaia-button>
      </template>
    </gaia-empty-state>

    <!-- The grid stays mounted while a month loads (veil, `v-show`): n-calendar
         holds the visible month itself and would reset to today on remount. -->
    <n-flex v-show="!loadError" vertical :size="10" :wrap="false">
      <template v-if="!loading">
        <gaia-text v-if="events.length === 0 && hasFilters" size="meta" tone="subtle">
          No dates this month match this search — clear the search or the filters to see every date.
        </gaia-text>
        <gaia-text v-else-if="events.length === 0" size="meta" tone="subtle">
          No dates this month — fixed dates appear once the contracts in scope have them, extracted
          dates once Insights has run this calendar’s prompt.
        </gaia-text>
      </template>

      <gaia-spinner :show="loading">
        <gaia-calendar @panel-change="onPanelChange">
          <template #header="{ year, month }">
            <gaia-text size="subtitle" weight="bold" tone="ink">
              {{ dayjs(new Date(year, month - 1, 1)).format('MMMM') }}
            </gaia-text>
            <gaia-text size="subtitle" tone="subtle">&nbsp;{{ year }}</gaia-text>
          </template>

          <template #default="{ year, month, date }">
            <div class="day">
              <button
                v-for="event in (eventsByDay.get(dayKey(year, month, date)) ?? []).slice(
                  0,
                  VISIBLE_PER_DAY
                )"
                :key="event.key"
                type="button"
                class="chip"
                :style="chipStyle(event)"
                :title="`${event.title} · ${contractName(event)}`"
                @click.stop="openEvent($event, dayKey(year, month, date), event)"
              >
                {{ event.title }} · {{ contractName(event) }}
              </button>
              <button
                v-if="(eventsByDay.get(dayKey(year, month, date))?.length ?? 0) > VISIBLE_PER_DAY"
                type="button"
                class="more"
                @click.stop="openDay($event, dayKey(year, month, date))"
              >
                +{{ (eventsByDay.get(dayKey(year, month, date))?.length ?? 0) - VISIBLE_PER_DAY }}
                more
              </button>
            </div>
          </template>
        </gaia-calendar>
      </gaia-spinner>
    </n-flex>

    <gaia-popover
      trigger="manual"
      placement="bottom-start"
      :show="popover.show"
      :x="popover.x"
      :y="popover.y"
      @clickoutside="closePopover"
    >
      <gaia-menu-surface width="300px">
        <n-flex
          v-if="popover.event"
          vertical
          :size="8"
          :wrap="false"
          :style="{ padding: '6px 8px' }"
        >
          <gaia-text size="body" weight="semibold" tone="ink">{{ popover.event.title }}</gaia-text>
          <gaia-text size="meta" tone="soft">
            {{ dayjs(popover.day).format('dddd, D MMMM YYYY') }}
          </gaia-text>
          <gaia-text v-if="recurrenceText(popover.event.recurrence)" size="meta" tone="subtle">
            {{ recurrenceText(popover.event.recurrence) }}
          </gaia-text>
          <n-flex vertical :size="2" :wrap="false" :style="{ minWidth: 0 }">
            <gaia-link
              v-if="popover.event.file.contract"
              :underline="false"
              :to="
                contractBlock(popover.event)
                  ? undefined
                  : contractRoute(popover.event.file.contract.uid)
              "
              :disabled="contractBlock(popover.event) !== null"
              :title="contractBlock(popover.event) ?? undefined"
              :style="{ textAlign: 'left' }"
            >
              {{ popover.event.file.contract.name }}
            </gaia-link>
            <gaia-link
              tone="muted"
              :underline="false"
              :to="fileBlock(popover.event) ? undefined : contractFileRoute(popover.event.file.uid)"
              :disabled="fileBlock(popover.event) !== null"
              :title="fileBlock(popover.event) ?? undefined"
              :style="{ textAlign: 'left' }"
            >
              {{ fileName(popover.event) }}
            </gaia-link>
          </n-flex>
          <gaia-button
            v-if="popover.event.extraction"
            size="small"
            :disabled="extractionBlock(popover.event) !== null"
            :title="extractionBlock(popover.event) ?? undefined"
            :style="{ alignSelf: 'flex-start' }"
            @click="editExtraction(popover.event)"
          >
            Edit extraction
          </gaia-button>
        </n-flex>

        <n-flex v-else vertical :size="2" :wrap="false">
          <gaia-text variant="label" size="micro" tone="faint" :style="{ padding: '6px 8px 4px' }">
            {{ dayjs(popover.day).format('dddd, D MMMM') }}
          </gaia-text>
          <button
            v-for="event in popoverDayEvents"
            :key="event.key"
            type="button"
            class="chip"
            :style="chipStyle(event)"
            @click.stop="openEvent($event, popover.day, event)"
          >
            {{ event.title }} · {{ contractName(event) }}
          </button>
        </n-flex>
      </gaia-menu-surface>
    </gaia-popover>

    <contract-management-extraction-details-modal
      v-model:show="showEditModal"
      :cell-data="editingCell"
      :on-data-update="handleExtractionUpdate"
    />
  </n-flex>
</template>

<style scoped>
/* n-calendar fixes its height at 720px. Inside the spinner's block wrapper that
   is strict, so a busy week would spill out below the grid; grow with the
   chips instead, keeping 720px as the floor. */
:deep(.n-calendar) {
  height: auto;
  min-height: 720px;
}

.day {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  min-width: 0;
}

/* One entry in a day cell, Apple-Calendar style: a coloured bar and a single
   truncated line. Extracted dates take the accent; fixed dates stay neutral. */
.chip {
  display: block;
  width: 100%;
  padding: 1px 6px;
  border: none;
  border-left: 3px solid transparent;
  border-radius: 3px;
  background: none;
  font: inherit;
  font-size: 11.5px;
  line-height: 1.5;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.chip:hover {
  box-shadow: inset 0 0 0 100px v-bind(chipHover);
}

.more {
  padding: 0 6px;
  border: none;
  background: none;
  font: inherit;
  font-size: 11.5px;
  text-align: left;
  color: v-bind(moreText);
  cursor: pointer;
}

.more:hover {
  color: v-bind(moreHover);
}
</style>
